import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  orderBy,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "@/api/firebase/firebase";
import {
  Client,
  CreateClientRequest,
  UpdateClientRequest,
} from "@/types/client";

const CLIENTS_COLLECTION = "clients";
const STORAGE_PATH = "client-logos";

export class ClientService {
  // Upload image to Firebase Storage
  private static async uploadImage(
    file: File,
    clientId: string,
    type: "full" | "icon"
  ): Promise<string> {
    try {
      const fileName = `${clientId}-${type}-logo-${Date.now()}`;
      const storageRef = ref(storage, `${STORAGE_PATH}/${fileName}`);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      console.error(`Error uploading ${type} logo:`, error);
      throw error;
    }
  }

  // Delete image from Firebase Storage
  private static async deleteImage(imageUrl: string): Promise<void> {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error) {
      console.warn("Failed to delete image:", error);
      // Don't throw error for missing images
    }
  }

  // Create a new client
  static async createClient(clientData: CreateClientRequest): Promise<Client> {
    try {
      // First create the client document to get an ID
      const docRef = await addDoc(collection(db, CLIENTS_COLLECTION), {
        name: clientData.name,
        location: clientData.location,
        contactPerson: clientData.contactPerson,
        email: clientData.email,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      let fullLogoUrl = "";
      let iconLogoUrl = "";

      // Upload images if provided
      if (clientData.fullLogo) {
        fullLogoUrl = await this.uploadImage(
          clientData.fullLogo,
          docRef.id,
          "full"
        );
      }

      if (clientData.iconLogo) {
        iconLogoUrl = await this.uploadImage(
          clientData.iconLogo,
          docRef.id,
          "icon"
        );
      }

      // Update the document with image URLs
      if (fullLogoUrl || iconLogoUrl) {
        await updateDoc(docRef, {
          ...(fullLogoUrl && { fullLogoUrl }),
          ...(iconLogoUrl && { iconLogoUrl }),
          updatedAt: Timestamp.fromDate(new Date()),
        });
      }

      // Return the created client
      const createdDoc = await getDoc(docRef);
      const data = createdDoc.data()!;

      return {
        id: docRef.id,
        name: data.name,
        location: data.location,
        contactPerson: data.contactPerson,
        email: data.email,
        fullLogoUrl: data.fullLogoUrl,
        iconLogoUrl: data.iconLogoUrl,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      };
    } catch (error) {
      console.error("Error creating client:", error);
      throw new Error("Failed to create client. Please try again.");
    }
  }

  // Get all clients
  static async getClients(): Promise<Client[]> {
    try {
      const q = query(
        collection(db, CLIENTS_COLLECTION),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          location: data.location,
          contactPerson: data.contactPerson,
          email: data.email,
          fullLogoUrl: data.fullLogoUrl,
          iconLogoUrl: data.iconLogoUrl,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        };
      });
    } catch (error) {
      console.error("Error fetching clients:", error);
      throw new Error("Failed to load clients. Please try again.");
    }
  }

  // Get a single client by ID
  static async getClient(id: string): Promise<Client | null> {
    try {
      const docRef = doc(db, CLIENTS_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name,
        location: data.location,
        contactPerson: data.contactPerson,
        email: data.email,
        fullLogoUrl: data.fullLogoUrl,
        iconLogoUrl: data.iconLogoUrl,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      };
    } catch (error) {
      console.error("Error fetching client:", error);
      throw new Error("Failed to load client. Please try again.");
    }
  }

  // Update a client
  static async updateClient(clientData: UpdateClientRequest): Promise<Client> {
    try {
      const docRef = doc(db, CLIENTS_COLLECTION, clientData.id);
      const currentDoc = await getDoc(docRef);

      if (!currentDoc.exists()) {
        throw new Error("Client not found");
      }

      const currentData = currentDoc.data();
      let fullLogoUrl = currentData.fullLogoUrl;
      let iconLogoUrl = currentData.iconLogoUrl;

      // Handle full logo update
      if (clientData.fullLogo) {
        // Delete old logo if exists
        if (fullLogoUrl) {
          await this.deleteImage(fullLogoUrl);
        }
        fullLogoUrl = await this.uploadImage(
          clientData.fullLogo,
          clientData.id,
          "full"
        );
      }

      // Handle icon logo update
      if (clientData.iconLogo) {
        // Delete old logo if exists
        if (iconLogoUrl) {
          await this.deleteImage(iconLogoUrl);
        }
        iconLogoUrl = await this.uploadImage(
          clientData.iconLogo,
          clientData.id,
          "icon"
        );
      }

      // Update the document
      const updateData: any = {
        updatedAt: Timestamp.fromDate(new Date()),
      };

      if (clientData.name !== undefined) updateData.name = clientData.name;
      if (clientData.location !== undefined)
        updateData.location = clientData.location;
      if (clientData.contactPerson !== undefined)
        updateData.contactPerson = clientData.contactPerson;
      if (clientData.email !== undefined) updateData.email = clientData.email;
      if (fullLogoUrl !== currentData.fullLogoUrl)
        updateData.fullLogoUrl = fullLogoUrl;
      if (iconLogoUrl !== currentData.iconLogoUrl)
        updateData.iconLogoUrl = iconLogoUrl;

      await updateDoc(docRef, updateData);

      // Return updated client
      const updatedDoc = await getDoc(docRef);
      const data = updatedDoc.data()!;

      return {
        id: clientData.id,
        name: data.name,
        location: data.location,
        contactPerson: data.contactPerson,
        email: data.email,
        fullLogoUrl: data.fullLogoUrl,
        iconLogoUrl: data.iconLogoUrl,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      };
    } catch (error) {
      console.error("Error updating client:", error);
      throw new Error("Failed to update client. Please try again.");
    }
  }

  // Delete a client
  static async deleteClient(id: string): Promise<void> {
    try {
      const docRef = doc(db, CLIENTS_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Client not found");
      }

      const data = docSnap.data();

      // Delete associated images
      if (data.fullLogoUrl) {
        await this.deleteImage(data.fullLogoUrl);
      }
      if (data.iconLogoUrl) {
        await this.deleteImage(data.iconLogoUrl);
      }

      // Delete the document
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting client:", error);
      throw new Error("Failed to delete client. Please try again.");
    }
  }
}
